import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  copiarFotoDoChecklistParaFornecedor,
  enviarFotoParaFornecedor,
  fetchFotosDoVeiculo,
  fetchFotosParaFornecedor,
  FotoDeEntrada,
  FotoParaFornecedor,
  removerFotoParaFornecedor,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import { CotacaoItem } from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
import { describeRejectedFiles, prepareMediaFiles } from '../../../utils/mediaFilePreparation';
import ConfirmacaoModal from './ConfirmacaoModal';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/feedback/aviso-de-tela.css';
import '../../../components/modal/contac/filtro/ModalFiltro.css'; // `premium-text-input`, por import (BUG-V74)

/**
 * FOTOS QUE O FORNECEDOR VÊ (portal-cotacoes/at-17).
 *
 * Pedido da Alessandra e do Phelipe no teste de Compras de 29/10/2026: além da foto do veículo da
 * prévia, fotos de dano e de veículo em oficina externa (que não tem checklist no SOAC). Aparecem na
 * tela do fornecedor; com peça escolhida, aparecem também naquela peça.
 *
 * 🔴 É o OPOSTO da seção de imagens internas logo acima, e a seção diz isso em faixa fixa: as duas
 * aceitam foto, e confundir uma com a outra mandaria ao fornecedor uma foto que era só da empresa.
 */

/** Teto da foto, alinhado ao `FileInterceptor` do backend (10 MB). */
const MAX_FOTO_BYTES = 10 * 1024 * 1024;
/** Quantas fotos a cotação pode ter para o fornecedor (o backend recusa acima disto). */
const MAX_FOTOS = 10;

interface FotosFornecedorSectionProps {
  cotacaoId: string;
  itens: CotacaoItem[];
  /** Quem pode enviar e remover (o TI lê e não opera, ADR 0012), e só enquanto a cotação recebe respostas. */
  podeEditar: boolean;
}

const FotosFornecedorSection: React.FC<FotosFornecedorSectionProps> = ({ cotacaoId, itens, podeEditar }) => {
  const [fotos, setFotos] = useState<FotoParaFornecedor[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [itemEscolhido, setItemEscolhido] = useState('');
  const [aRemover, setARemover] = useState<FotoParaFornecedor | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /*
   * Fotos do checklist para copiar. `null` = ainda não abertas; o estado de erro é separado, para
   * "o checklist não tem foto" e "não consegui consultar" não se confundirem (at-60).
   */
  const [doChecklist, setDoChecklist] = useState<FotoDeEntrada[] | null>(null);
  const [checklistIndisponivel, setChecklistIndisponivel] = useState(false);
  const [erroChecklist, setErroChecklist] = useState(false);
  const [abrindoChecklist, setAbrindoChecklist] = useState(false);

  async function abrirChecklist() {
    if (doChecklist) {
      setDoChecklist(null);
      return;
    }
    setAbrindoChecklist(true);
    try {
      const r = await fetchFotosDoVeiculo(cotacaoId);
      setChecklistIndisponivel(!r.disponivel);
      setDoChecklist(r.fotos);
      setErroChecklist(false);
    } catch (error) {
      setErroChecklist(true);
      notify.errorFrom(error, 'Não foi possível consultar as fotos do checklist.');
    } finally {
      setAbrindoChecklist(false);
    }
  }

  async function copiar(midiaId: string) {
    setEnviando(true);
    try {
      await copiarFotoDoChecklistParaFornecedor(cotacaoId, midiaId, itemEscolhido || undefined);
      notify.success('Foto do checklist enviada para os fornecedores.');
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível copiar a foto do checklist.');
    } finally {
      setEnviando(false);
    }
  }

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setFotos(await fetchFotosParaFornecedor(cotacaoId));
      setErro(false);
    } catch (error) {
      // 🔴 Lista vazia por erro seria lida como "o fornecedor não vê foto nenhuma" (at-60).
      setFotos([]);
      setErro(true);
      notify.errorFrom(error, 'Não foi possível carregar as fotos para o fornecedor. Recarregue a página; se persistir, acione a TI.');
    } finally {
      setCarregando(false);
    }
  }, [cotacaoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function escolherArquivos(e: React.ChangeEvent<HTMLInputElement>) {
    const escolhidos = Array.from(e.target.files ?? []);
    if (inputRef.current) inputRef.current.value = '';
    if (escolhidos.length === 0) return;

    setEnviando(true);
    try {
      // O mesmo preparo das imagens internas: HEIC do iPhone vira JPEG, o que não é imagem é recusado.
      const { ready, rejected } = await prepareMediaFiles(escolhidos, { allowVideo: false, allowPdf: false });
      if (rejected.length > 0) notify.warning(describeRejectedFiles(rejected));

      const vagas = Math.max(0, MAX_FOTOS - fotos.length);
      const cabem = ready.filter((f) => f.size <= MAX_FOTO_BYTES).slice(0, vagas);
      if (ready.some((f) => f.size > MAX_FOTO_BYTES)) notify.warning('Fotos acima de 10 MB não foram enviadas.');
      if (ready.length > cabem.length && vagas < ready.length) {
        notify.warning(`A cotação aceita até ${MAX_FOTOS} fotos para o fornecedor; as que passaram disso não foram enviadas.`);
      }

      let enviadas = 0;
      for (const arquivo of cabem) {
        try {
          await enviarFotoParaFornecedor(cotacaoId, arquivo, itemEscolhido || undefined);
          enviadas += 1;
        } catch (error) {
          notify.errorFrom(error, `Não foi possível enviar "${arquivo.name}".`);
        }
      }
      if (enviadas > 0) {
        notify.success(enviadas === 1 ? 'Foto enviada para os fornecedores.' : `${enviadas} fotos enviadas para os fornecedores.`);
        await carregar();
      }
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível preparar as fotos escolhidas.');
    } finally {
      setEnviando(false);
    }
  }

  async function confirmarRemocao() {
    if (!aRemover) return;
    setEnviando(true);
    try {
      await removerFotoParaFornecedor(aRemover.id);
      notify.success('Foto removida. O fornecedor deixa de vê-la.');
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível remover a foto.');
    } finally {
      setEnviando(false);
      setARemover(null);
    }
  }

  const nomeDoItem = (id: string | null) => (id ? itens.find((i) => i.id === id)?.descricao ?? 'Peça' : 'Veículo');

  return (
    <div className="pc-card">
      <h2 className="pc-card-titulo">Fotos para o fornecedor ({fotos.length} de {MAX_FOTOS})</h2>
      <div className="pc-card-corpo">
        <p className="aviso-de-tela" style={{ marginTop: 0 }}>
          <strong>Estas fotos aparecem para os fornecedores</strong> na tela da cotação, junto com a foto do veículo.
          Use para dano ou veículo em oficina externa. Foto só para a equipe vai em "Imagens (uso interno)".
        </p>

        {erro ? (
          <p className="erro-de-tela">
            Não foi possível carregar as fotos para o fornecedor. Pode haver fotos que ele vê e que não estão
            aparecendo aqui.
          </p>
        ) : carregando ? (
          <p className="pc-texto-secundario">Carregando...</p>
        ) : (
          <>
            {podeEditar && fotos.length < MAX_FOTOS && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {itens.length > 0 && (
                  <select
                    className="premium-text-input"
                    aria-label="Peça a que a foto se refere"
                    value={itemEscolhido}
                    disabled={enviando}
                    onChange={(e) => setItemEscolhido(e.target.value)}
                  >
                    <option value="">Foto do veículo (sem peça específica)</option>
                    {itens.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.descricao}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  className="pc-btn pc-btn--secundario pc-btn--sm"
                  disabled={enviando}
                  onClick={() => inputRef.current?.click()}
                >
                  {enviando ? 'Enviando...' : 'Enviar fotos'}
                </button>
                <button
                  type="button"
                  className="pc-btn pc-btn--secundario pc-btn--sm"
                  disabled={enviando || abrindoChecklist}
                  onClick={abrirChecklist}
                >
                  {abrindoChecklist ? 'Consultando...' : doChecklist ? 'Fechar checklist' : 'Copiar do checklist'}
                </button>
                <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={escolherArquivos} />
              </div>
            )}

            {erroChecklist && (
              <p className="erro-de-tela" style={{ marginTop: 12 }}>
                Não foi possível consultar as fotos do checklist. Tente de novo; se persistir, acione a TI.
              </p>
            )}
            {doChecklist && (
              <>
                {checklistIndisponivel ? (
                  <p className="pc-hint" style={{ marginTop: 12 }}>
                    Esta requisição não está ligada a um atendimento da oficina, então não há checklist de onde copiar.
                  </p>
                ) : doChecklist.length === 0 ? (
                  <p className="pc-hint" style={{ marginTop: 12 }}>
                    O checklist de entrada deste veículo não tem fotos.
                  </p>
                ) : (
                  <>
                    <p className="pc-texto-secundario" style={{ marginTop: 12, marginBottom: 0 }}>
                      Clique na foto que vai para os fornecedores:
                    </p>
                    <div className="pc-fotos-grade">
                      {doChecklist.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          className="pc-foto-opcao"
                          onClick={() => copiar(f.id)}
                          disabled={enviando || fotos.length >= MAX_FOTOS}
                          title={f.rotulo ?? 'Foto do checklist'}
                        >
                          <img src={f.url} alt={f.rotulo ?? 'Foto do checklist'} />
                          <span className="pc-foto-opcao-rotulo">{f.rotulo ?? 'Foto do checklist'}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {fotos.length === 0 ? (
              <p className="pc-texto-secundario" style={{ marginTop: 12 }}>
                Nenhuma foto além da foto do veículo.
              </p>
            ) : (
              <div className="pc-anexos-grade">
                {fotos.map((f) => (
                  <div key={f.id} className="pc-anexo">
                    {f.url ? (
                      <img
                        className="pc-anexo-miniatura"
                        src={f.url}
                        alt={f.nomeOriginal}
                        title="Abrir em nova aba"
                        onClick={() => window.open(f.url!, '_blank', 'noopener')}
                      />
                    ) : (
                      <div className="pc-anexo-sem-previa">Prévia indisponível agora</div>
                    )}
                    <div className="pc-anexo-info">
                      <span className="pc-anexo-nome" title={f.nomeOriginal}>
                        {f.nomeOriginal}
                      </span>
                      <span className="pc-anexo-meta" title={nomeDoItem(f.cotacaoItemId)}>
                        {nomeDoItem(f.cotacaoItemId)} · {f.origem === 'CHECKLIST' ? 'do checklist' : 'enviada'}
                      </span>
                      <span className="pc-anexo-meta">
                        {f.enviadoPorNome ?? 'autor não registrado'} · {new Date(f.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                      {podeEditar && (
                        <button type="button" className="pc-anexo-remover" disabled={enviando} onClick={() => setARemover(f)}>
                          Remover
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmacaoModal
        isOpen={aRemover !== null}
        titulo="Remover esta foto?"
        descricao={
          <p style={{ margin: '0 0 10px' }}>
            <strong>{aRemover?.nomeOriginal}</strong> deixa de aparecer para os fornecedores. O registro de que a foto
            existiu fica na auditoria.
          </p>
        }
        rotuloConfirmar="Remover"
        carregando={enviando}
        onConfirmar={confirmarRemocao}
        onCancelar={() => setARemover(null)}
      />
    </div>
  );
};

export default FotosFornecedorSection;
